"use client";

import type { PlaceholderValues, Rating } from "@homeinstead/schemas";
import type { FormThankYou } from "./form-types";

import { useEffect, useRef } from "react";
import { BlockDebugDev } from "~www/components/PageBuilder/BlockDebugDev";
import { ThankYouItemRenderer } from "./ThankYouItemRenderer";
import { ThankYouSuccessIcon } from "./ThankYouSuccessIcon";

export function ThankYou({
  thankYou,
  placeholderValues,
  phoneNumber,
  officeRating,
}: {
  thankYou?: FormThankYou | null;
  placeholderValues?: PlaceholderValues;
  phoneNumber?: string;
  officeRating?: Omit<Rating, "_type">;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo({ behavior: "smooth", top: 0 });

    // When the fullscreen takeover is open, a successful submit
    // unmounts the Dialog along with everything else — an abrupt parent
    // unmount rather than a normal Radix close, so focus does not get
    // restored to a trigger and falls to <body>. Claim it here so a
    // screen-reader user lands somewhere meaningful instead of nowhere.
    containerRef.current?.focus();
  }, []);

  const content = thankYou?.content ?? [];
  const showSuccessIcon = thankYou?.icon === "success";

  return (
    <div
      className="flex w-full flex-col items-center gap-4 px-6 font-jakarta text-new-beginnings-base outline-none"
      ref={containerRef}
      tabIndex={-1}
    >
      {showSuccessIcon ? <ThankYouSuccessIcon /> : null}

      <div className="flex w-full max-w-[560px] flex-col items-start gap-6 pb-20 [&_p+p]:mt-2">
        {content.length > 0 ? (
          content.map((item) => (
            <div className="w-full" key={item._key}>
              <ThankYouItemRenderer
                input={item}
                officeRating={officeRating}
                phoneNumber={phoneNumber}
                placeholderValues={placeholderValues}
              />
            </div>
          ))
        ) : (
          <BlockDebugDev
            blockType="thankYou"
            message="No thank-you content configured"
          />
        )}
      </div>
    </div>
  );
}
